import { lazy, ComponentType } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Lazy import with auto-recovery from stale chunks after deploys.
 *
 * - Detects chunk-load failures (typical after a deploy when the old hashed
 *   filenames are gone)
 * - Logs failure to `lazy_chunk_failures` for telemetry (best-effort)
 * - Dispatches `zooi:lazy-chunk-failed` so a UI banner can show
 *   "Sidan uppdateras…" before the reload
 * - Reloads the page ONCE per session (`sessionStorage` flag) so the user
 *   gets the fresh asset manifest, but we never get stuck in a reload loop
 *   if the failure is genuine (server outage, network issue).
 *
 * Usage:
 *   const MyPage = lazyWithRetry(() => import("@/pages/MyPage"));
 *
 * Component name is inferred from the import path. Provide a second arg to
 * override (recommended for re-exports like `.then(m => ({ default: m.X }))`).
 */

const RELOAD_KEY = "zooi:chunk-reload";
const SESSION_KEY = "zooi:session-id";

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "no-session";
  }
}

/**
 * Best-effort extraction of the import path from the factory's source.
 * Works in both dev and (minified) prod because Vite preserves the
 * `import("...")` literal inside the arrow.
 */
function inferChunkName(factory: () => unknown): string {
  try {
    const src = factory.toString();
    const m = src.match(/import\(["']([^"']+)["']\)/);
    if (m && m[1]) return m[1];
  } catch {
    /* ignore */
  }
  return "unknown";
}

async function reportFailure(params: {
  chunkName: string;
  error: unknown;
  willReload: boolean;
}) {
  if (typeof window === "undefined") return;
  const { chunkName, error, willReload } = params;
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack ?? null : null;

  // Local breadcrumb — visible in DevTools console regardless of network
  // eslint-disable-next-line no-console
  console.warn(
    `[lazyWithRetry] chunk load failed: ${chunkName} — ${message}${willReload ? " (reloading)" : " (giving up)"}`,
  );

  // Custom event so a banner component can react instantly (before reload)
  try {
    window.dispatchEvent(
      new CustomEvent("zooi:lazy-chunk-failed", {
        detail: { chunkName, message, willReload },
      }),
    );
  } catch {
    /* ignore */
  }

  // Fire-and-forget telemetry insert. RLS allows anon inserts on this table.
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase.from("lazy_chunk_failures" as any) as any).insert({
      chunk_name: chunkName,
      error_message: message,
      error_stack: stack,
      user_agent: navigator.userAgent,
      page_url: window.location.href,
      will_reload: willReload,
      session_id: getSessionId(),
    });
  } catch {
    /* network may be down — telemetry is best-effort only */
  }
}

function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  nameOverride?: string,
) {
  const chunkName = nameOverride ?? inferChunkName(factory);

  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (!isChunkLoadError(err)) throw err;

      if (typeof window === "undefined") throw err;

      const already = (() => {
        try {
          return sessionStorage.getItem(RELOAD_KEY);
        } catch {
          return null;
        }
      })();

      if (!already) {
        try {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        } catch {
          /* ignore */
        }
        await reportFailure({ chunkName, error: err, willReload: true });
        // Tiny delay so the banner can render briefly before reload
        setTimeout(() => window.location.reload(), 600);
        // Suspend forever — Suspense fallback (banner) stays visible
        return new Promise(() => {}) as Promise<{ default: T }>;
      }

      // Second failure in the same session — surface the error so the
      // ErrorBoundary can show a real message instead of looping reloads.
      await reportFailure({ chunkName, error: err, willReload: false });
      throw err;
    }
  });
}
